import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, LanguageTabs, Loading, ImageUpload } from '../components/ui';
import { useCrud } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface Collaborator {
  id: number;
  name: string;
  role_en: string;
  role_ro: string;
  icon: string;
  avatar_url?: string;
  sort_order: number;
}

const CollaboratorsPage: React.FC = () => {
  const collaborators = useCrud<Collaborator>('/api/collaborators');
  const { showToast } = useToast();
  const [editingCollab, setEditingCollab] = useState<Collaborator | null>(null);

  useEffect(() => { collaborators.fetchAll(); }, []);

  const handleSave = async (collab: Partial<Collaborator>) => {
    if (editingCollab?.id) {
      const result = await collaborators.update(editingCollab.id, collab);
      if (result) {
        showToast('Collaborator updated!', 'success');
        setEditingCollab(null);
      }
    } else {
      const result = await collaborators.create(collab);
      if (result) {
        showToast('Collaborator added!', 'success');
        setEditingCollab(null);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this collaborator?')) {
      const result = await collaborators.remove(id);
      if (result) showToast('Collaborator deleted', 'success');
    }
  };

  if (collaborators.loading && collaborators.items.length === 0) return <Loading />;

  return (
    <>
      <PageHeader
        title="Collaborators"
        subtitle="Manage the 'Worked With' section"
        actions={
          <Button onClick={() => setEditingCollab({ id: 0, name: '', role_en: 'Artist', role_ro: 'Artist', icon: '🎤', sort_order: collaborators.items.length })}>
            + Add Collaborator
          </Button>
        }
      />

      <div className="admin-content">
        <Card>
          <div className="admin-list-editor">
            {collaborators.items.map(collab => (
              <div key={collab.id} className="admin-list-item">
                {collab.avatar_url ? (
                  <img src={collab.avatar_url} alt={collab.name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '1.5rem' }}>{collab.icon}</span>
                )}
                <div className="admin-list-item-content">
                  <div>
                    <strong>{collab.name}</strong>
                    <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>{collab.role_en}</div>
                  </div>
                </div>
                <div className="admin-list-item-actions">
                  <Button size="sm" variant="secondary" onClick={() => setEditingCollab(collab)}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(collab.id)}>Delete</Button>
                </div>
              </div>
            ))}
            {collaborators.items.length === 0 && (
              <p style={{ color: 'var(--admin-text-muted)', textAlign: 'center', padding: 20 }}>
                No collaborators yet. Add your first collaborator above.
              </p>
            )}
          </div>
        </Card>

        {editingCollab && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }} onClick={() => setEditingCollab(null)}>
            <div style={{ background: 'var(--admin-surface)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginBottom: 20 }}>{editingCollab.id ? 'Edit Collaborator' : 'Add Collaborator'}</h3>
              <CollaboratorForm
                collaborator={editingCollab}
                onSave={handleSave}
                onCancel={() => setEditingCollab(null)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const CollaboratorForm: React.FC<{
  collaborator: Collaborator;
  onSave: (c: Partial<Collaborator>) => void;
  onCancel: () => void;
}> = ({ collaborator, onSave, onCancel }) => {
  const [form, setForm] = useState(collaborator);
  const [lang, setLang] = useState<'en' | 'ro'>('en');

  return (
    <div className="admin-form">
      <Input label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
      <Input label="Icon (emoji)" value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="🎤" />
      <ImageUpload label="Avatar (optional)" value={form.avatar_url} onChange={url => setForm(f => ({ ...f, avatar_url: url || undefined }))} />

      <LanguageTabs currentLang={lang} onChange={setLang} />
      {lang === 'en' ? (
        <Input label="Role (EN)" value={form.role_en} onChange={e => setForm(f => ({ ...f, role_en: e.target.value }))} placeholder="Artist" />
      ) : (
        <Input label="Role (RO)" value={form.role_ro} onChange={e => setForm(f => ({ ...f, role_ro: e.target.value }))} placeholder="Artist" />
      )}

      <Input label="Sort Order" type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />

      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <Button onClick={() => onSave(form)}>Save</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default CollaboratorsPage;
